import React, { useState, useRef } from 'react';

export default function ContactForm({ defaultMessage = '' }) {
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const formRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Client-side validation
    const totalFiles = selectedFiles.length + files.length;
    if (totalFiles > 5) {
      alert('Du kan højst vedhæfte 5 billeder i alt.');
      return;
    }

    const oversized = files.some(file => file.size > 10 * 1024 * 1024); // 10MB
    if (oversized) {
      alert('En eller flere filer overskrider størrelsesgrænsen på 10 MB.');
      return;
    }

    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const form = formRef.current;
    const formData = new FormData();

    // Append standard fields
    formData.append('name', form.name.value);
    formData.append('phone', form.phone.value);
    formData.append('email', form.email.value);
    formData.append('message', form.message.value);
    formData.append('website_url', form.website_url.value); // Honeypot

    // Append files
    selectedFiles.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus('success');
        setSelectedFiles([]);
        form.reset();

        // 1. Send GTM dataLayer events for flexible tracking in Tag Manager
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'generate_lead',
            event_category: 'Formular',
            event_label: 'Kontaktformular'
          });
          window.dataLayer.push({
            event: 'contact_form_submit',
            form_id: 'contact_form'
          });
        }

        // 2. Send Meta Pixel Lead event
        if (window.fbq) {
          window.fbq('track', 'Lead');
        }
      } else {
        setStatus('error');
        setErrorMessage(result.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Kunne ikke oprette forbindelse til serveren. Kontroller din internetforbindelse.');
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-6 md:p-8 shadow-sm">
      {status === 'success' ? (
        <div className="text-center py-12 flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-brand-green/10 border border-brand-green flex items-center justify-center text-brand-green text-3xl animate-bounce">
            <i className="fa-solid fa-circle-check" />
          </div>
          <h3 className="text-2xl font-bold text-zinc-900">Tak for din henvendelse!</h3>
          <p className="text-zinc-600 max-w-md leading-relaxed text-sm">
            Vi har modtaget din besked og eventuelle billeder. Vi vender tilbage til dig med en professionel vurdering og et uforpligtende tilbud hurtigst muligt.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-semibold px-6 py-2.5 rounded-lg text-xs uppercase tracking-wider transition-all"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Honeypot - lad være med at udfylde dette felt</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Navn */}
            <div className="flex flex-col gap-2">
              <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-zinc-700">
                Navn <span className="text-brand-green">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                placeholder="Dit fulde navn"
                className="bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-brand-green focus:bg-white transition-colors"
              />
            </div>

            {/* Telefonnummer */}
            <div className="flex flex-col gap-2">
              <label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-zinc-700">
                Telefonnummer <span className="text-brand-green">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                placeholder="Dit telefonnummer"
                className="bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-brand-green focus:bg-white transition-colors"
              />
            </div>
          </div>

          {/* Email */}
          <div className="flex flex-col gap-2">
            <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-zinc-700">
              E-mail <span className="text-brand-green">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              placeholder="din.email@adresse.dk"
              className="bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-brand-green focus:bg-white transition-colors"
            />
          </div>

          {/* Beskrivelse */}
          <div className="flex flex-col gap-2">
            <label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-zinc-700">
              Beskrivelse af opgaven <span className="text-brand-green">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows="5"
              defaultValue={defaultMessage}
              placeholder="Beskriv her, hvad der skal renses, og angiv gerne det omtrentlige omfang i kvadratmeter..."
              className="bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-brand-green focus:bg-white transition-colors resize-none leading-relaxed"
            />
          </div>

          {/* File Upload */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-700">
              Vedhæft billeder (Valgfrit)
            </label>
            <div className="border-2 border-dashed border-zinc-200 hover:border-brand-green rounded-lg p-6 text-center transition-colors relative cursor-pointer bg-zinc-50">
              <input
                type="file"
                id="file-upload"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="flex flex-col items-center gap-2">
                <i className="fa-regular fa-cloud-arrow-up text-3xl text-zinc-400" />
                <span className="text-sm font-semibold text-zinc-700">Klik eller træk filer hertil</span>
                <span className="text-xs text-zinc-500">
                  Maks. 5 filer · Op til 10 MB pr. fil · Formater: JPG, PNG, WEBP, PDF
                </span>
              </div>
            </div>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {selectedFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-2 text-xs">
                    <div className="flex items-center gap-2 text-zinc-700 truncate">
                      <i className="fa-regular fa-file-image text-brand-green" />
                      <span className="truncate">{file.name}</span>
                      <span className="text-zinc-500 shrink-0">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-zinc-400 hover:text-red-500 p-1 cursor-pointer"
                    >
                      <i className="fa-solid fa-trash-can" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Consent Checkbox */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="consent"
              required
              className="mt-1 accent-brand-green cursor-pointer"
            />
            <label htmlFor="consent" className="text-xs text-zinc-500 leading-relaxed cursor-pointer select-none">
              Jeg accepterer, at ECOLASER behandler mine indtastede oplysninger for at besvare min henvendelse. Ved at indsende denne formular accepterer du, at ECOLASER behandler dine oplysninger i overensstemmelse med vores privatlivspolitik.
            </label>
          </div>

          {/* Error Message */}
          {status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-xs text-red-600 flex items-start gap-3">
              <i className="fa-solid fa-circle-exclamation text-base shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className={`w-full bg-brand-green hover:bg-brand-green-light text-zinc-950 font-bold py-4 rounded-lg text-sm uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer ${
              status === 'submitting' ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-[0_0_20px_rgba(118,184,42,0.3)]'
            }`}
          >
            {status === 'submitting' ? (
              <>
                <i className="fa-solid fa-spinner animate-spin" />
                Sender besked...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" />
                Indsend formular
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
